/**
 * AIUsageLine (VH-280)
 *
 * One feature's OpenAI calls over the last seven days next to the seven before, from the
 * ai_calls log. This is what says whether a switched model is slower or fails more often
 * than its predecessor — the check the spec asks for after every production switch.
 */
import { useTranslation } from 'react-i18next'
import { formatMs, formatRate, formatUsd } from './aiFormat'

function Period({ label, period, locale, t }) {
  if (!period || period.calls === 0) {
    return <span>{label}: {t('admin.aiModels.usage.noCalls')}</span>
  }
  const cost = formatUsd(period.cost_usd, locale)
  return (
    <span>
      {label}: {t('admin.aiModels.usage.calls', { count: period.calls })}
      {' · '}{period.cost_known ? cost : t('admin.aiModels.usage.costAtLeast', { cost })}
      {' · '}{t('admin.aiModels.usage.errors', { rate: formatRate(period.error_rate ?? 0, locale) })}
      {' · '}{t('admin.aiModels.usage.median', { time: formatMs(period.median_ms, locale) })}
      {period.fallbacks > 0 && (
        <span className="text-red-700">
          {' · '}{t('admin.aiModels.usage.fallbacks', { count: period.fallbacks })}
        </span>
      )}
    </span>
  )
}

export default function AIUsageLine({ usage }) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language || 'en'
  if (!usage) return null
  return (
    <p className="text-xs text-gray-500 flex flex-col sm:flex-row sm:flex-wrap sm:gap-x-3"
      data-testid={`ai-usage-${usage.key}`}>
      <Period label={t('admin.aiModels.usage.thisWeek')} period={usage.this_week} locale={locale} t={t} />
      <Period label={t('admin.aiModels.usage.lastWeek')} period={usage.last_week} locale={locale} t={t} />
      {usage.this_week?.calls > 0 && (
        <span>{t('admin.aiModels.usage.monthly', { cost: formatUsd(usage.monthly_estimate_usd, locale) })}</span>
      )}
    </p>
  )
}
